import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { HOMEPAGE_SUB_ROUTES } from './constants';
import FreeTrialUsageSummaryCard from '@/components/molecules/FreeTrial/FreeTrialUsageSummaryCard';
import useHomepageNavigationHook from '@/hooks/useHomepageNavigationHook';
import CheckoutStatusDialog from '@/Pages/Checkout/CheckoutStatusDialog';
import { useQueryParams } from '@/hooks/url/useQueryParams';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { currentUser } from '@/stores/user';
import { useAppContext } from '@/context/AppContext/AppContext';
import useAnalytics from '@/hooks/useAnalytics';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { isSimplifiedUploadEnabled } from '@/stores/clip';
import Icon, { IconName } from '@/components/atoms/Icon';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import { OPEN_NEW_CONTENT_MODAL_EVENT } from '@/components/organisms/NewContentModal/constants';

export default function Homepage(): JSX.Element {
  const [showCheckoutStatusDialog, setShowCheckoutStatusDialog] = useState(false);
  const { shouldNavigateToSplashPage } = useHomepageNavigationHook();
  const user = useSyncExternalStore(currentUser.subscribe, currentUser.getSnapshot);

  const { adminAppStore } = useAppContext();
  const { trackRecordingStudioClick } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useQueryParams<{ redirect: string }>();
  // Guards the splash-page modal dispatch so we only fire once per mount (the condition stays true
  // while no videos are uploaded, so without a guard it would re-dispatch on every re-render).
  const hasSplashModalDispatchedRef = useRef(false);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isNewHomePageEnabled = featureFlags[FeatureFlagKeys.Use_CL_New_HomePage];
  const isPlatformBrandKitEnabled = featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const { isFreeTrialEnabled, isFreemiumUser } = useFreeTrialHook();

  useEffect(() => {
    const importEventListener = EventBus.on(CustomEvents.NavigateToContentLabImport, () => {
      // Open the shell-level New Upload modal only when the simplified upload FF is on;
      // otherwise fall back to the legacy import page navigation.
      if (isSimplifiedUploadEnabled()) {
        document.dispatchEvent(new CustomEvent(OPEN_NEW_CONTENT_MODAL_EVENT));
      } else {
        navigate('/import');
      }
    });
    const editInContentLabEventListener = EventBus.on(
      CustomEvents.EditVideoAssetInContentLab,
      ({ contentId, projectId, url, activeDuplicateId }) => {
        if (url?.includes('import')) {
          navigate(`/import/${projectId}/${contentId}`);
          return;
        }

        if (activeDuplicateId) {
          navigate(`/${projectId}/${contentId}/recordings?activeDuplicateId=${encodeURIComponent(activeDuplicateId)}`);
          return;
        }

        navigate(`/${projectId}/${contentId}/recordings`);
      }
    );
    return () => {
      EventBus.off(CustomEvents.NavigateToContentLabImport, importEventListener);
      EventBus.off(CustomEvents.EditVideoAssetInContentLab, editInContentLabEventListener);
    };
  }, [navigate]);

  useEffect(() => {
    if (queryParams?.redirect) {
      if (queryParams.redirect === '/import' && isSimplifiedUploadEnabled()) {
        document.dispatchEvent(new CustomEvent(OPEN_NEW_CONTENT_MODAL_EVENT));
        navigate(location.pathname, { replace: true });
      } else {
        navigate(queryParams.redirect, { replace: true });
      }
    }
  }, [queryParams]);

  useEffect(() => {
    if (shouldNavigateToSplashPage && !location.pathname.startsWith('/import')) {
      if (isSimplifiedUploadEnabled()) {
        if (!hasSplashModalDispatchedRef.current) {
          hasSplashModalDispatchedRef.current = true;
          document.dispatchEvent(new CustomEvent(OPEN_NEW_CONTENT_MODAL_EVENT));
        }
      } else {
        navigate('/import');
      }
    }
  }, [location.pathname, shouldNavigateToSplashPage]);

  useEffect(() => {
    if (location.search.includes('checkout_session_id')) {
      setShowCheckoutStatusDialog(true);
    }
  }, [location.search]);

  const openCreateRecording = useCallback(() => {
    trackRecordingStudioClick();
    adminAppStore?.commit?.('eventRecordings/openCreateRecordingDialog');
  }, [adminAppStore, trackRecordingStudioClick]);

  const shouldShowRecordingStudioItem = useMemo(() => {
    return (
      (adminAppStore.currentOrg as any)?.isRecordingStudioPricingEnabled &&
      adminAppStore.orgPricingConfig?.pricing_config?.allowed_recording_event_count === undefined &&
      user?.is_content_lab_standalone
    );
  }, [adminAppStore.currentOrg, adminAppStore.orgPricingConfig, user?.is_content_lab_standalone]);

  const shouldShowSidebar = useMemo(() => {
    const useAIPageRevamp = Boolean(
      (adminAppStore.currentOrg as { useAIPageRevamp?: boolean } | undefined)?.useAIPageRevamp
    );
    // Vue-level ContentLabSideNav handles navigation for these users; suppress the React sidebar.
    // Mirrors the showContentLabSideNav condition in Organization.vue.
    if (user?.is_content_lab_standalone && useAIPageRevamp && isPlatformBrandKitEnabled) {
      return false;
    }

    // Always show the sidebar for Free Trial / Freemium users regardless of the new homepage flag
    if (isFreeTrialEnabled || isFreemiumUser) {
      return true;
    }

    return !isNewHomePageEnabled;
  }, [
    user?.is_content_lab_standalone,
    adminAppStore.currentOrg,
    isPlatformBrandKitEnabled,
    isFreemiumUser,
    isFreeTrialEnabled,
    isNewHomePageEnabled
  ]);

  const getNavLinkClassName = useCallback(
    (isActive: boolean): string => {
      const baseClasses = 'px-2 py-1 text-sm transition-colors';

      if (!isActive) {
        return `${baseClasses} hover:text-deep-orange-600`;
      }

      const activeClasses = isNewHomePageEnabled
        ? 'rounded bg-slate-100 text-deep-orange-600'
        : 'rounded bg-slate-100 font-semibold';

      return `${baseClasses} ${activeClasses}`;
    },
    [isNewHomePageEnabled]
  );

  if (shouldNavigateToSplashPage || !shouldShowSidebar) {
    return <Outlet />;
  }

  return (
    <div className="flex flex-row">
      <div className={`w-56 shrink-0 p-4 ${isNewHomePageEnabled ? 'bg-gray-100' : ''}`}>
        <div className="fixed flex w-48 flex-col space-y-3">
          {HOMEPAGE_SUB_ROUTES.filter(route => !(route.hideWhenNewHomepage && isNewHomePageEnabled)).map(route => (
            <NavLink key={route.title} to={route.url} className={({ isActive }) => getNavLinkClassName(isActive)}>
              {isNewHomePageEnabled ? (
                <div className="flex items-center gap-2">
                  <Icon name={route.icon as IconName} size={18} />
                  {route.title}
                </div>
              ) : (
                route.title
              )}
            </NavLink>
          ))}
          {shouldShowRecordingStudioItem && (
            <div
              className="flex cursor-pointer items-center gap-2 px-2 py-1 text-sm hover:text-deep-orange-600"
              onClick={openCreateRecording}
            >
              {isNewHomePageEnabled && <Icon name="IconMovie" size={18} />}
              <div>Recording Studio</div>
              <Icon name="IconLock" size={18} />
            </div>
          )}
        </div>
        <FreeTrialUsageSummaryCard />
        {showCheckoutStatusDialog && (
          <CheckoutStatusDialog
            isOpen={showCheckoutStatusDialog}
            onClose={() => {
              setShowCheckoutStatusDialog(false);
            }}
          />
        )}
      </div>
      <Outlet />
    </div>
  );
}
